import { ICapture, IPending } from '../shared/types'
import SuperCutStatus from '../../../types/super-cut-status'
import SuperCutUpload from '../../../types/super-cut-upload'
import styled from 'styled-components'
import React, { MouseEvent } from 'react'
import { PillBlock } from '../../blocks'
import { giphyDarkestGrey, giphyRed, giphyLightestGrey } from '@giphy/js-brand'
import dateFormat from 'dateformat'
import StatusBox from '../shared/components/status-box'
import SuperCutSegmentationType from '../../../../creation-tools/types/super-cut-segmentation-type'

const ROW_HEIGHT = 80

const Delete = styled.div`
    visibility: hidden;
    color: ${giphyRed};
    flex-shrink: 0;
    margin-right: 20px;
`
const CaptureRow = styled.a`
    display: flex;
    height: ${ROW_HEIGHT}px;
    background-color: ${giphyDarkestGrey};
    margin: 5px 0 5px 0;
    align-items: center;

    &:hover ${Delete} {
        visibility: visible;
    }
`

const PaddedPillBlock = styled(PillBlock)`
    margin-right: 5px;
`

const TitleContainer = styled.div`
    width: 100%;
    margin: 0 25px;
    height: ${ROW_HEIGHT}px;
    line-height: ${ROW_HEIGHT}px;
    min-width: 0;
    display: flex;
    align-items: center;
`

const CaptureTitle = styled.div`
    vertical-align: middle;
    font-weight: bold;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    margin-right: 10px;
`

const Detail = styled.div`
    font-size: 15px;
    font-weight: bold;
    display: inline-block;
    height: ${ROW_HEIGHT}px;
    line-height: ${ROW_HEIGHT}px;
    text-align: center;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    vertical-align: middle;
    flex-shrink: 0;
    margin-right: 30px;
`

const Icon = styled.i`
    font-size: 20px;
    color: ${giphyLightestGrey};
`

const Thumbnail = styled.div<{ src: string }>`
    height: ${ROW_HEIGHT}px;
    width: ${ROW_HEIGHT}px;
    background-image: url(${props => props.src});
    background-size: cover;
    background-repeat: no-repeat;
    flex-shrink: 0;
`

type PendingProps = {
    captures: IPending[]
}
export const PendingCaptureList = ({ captures }: PendingProps) => {
    return (
        <>
            {captures.map((p: IPending, i: number) => {
                const status = SuperCutStatus.getNormalizedStatus(p.status)
                const source = SuperCutUpload.getNormalizedUploadType(p.source)
                return (
                    <CaptureRow key={i}>
                        <TitleContainer>
                            <CaptureTitle>{p.title}</CaptureTitle>
                        </TitleContainer>
                        {p.process_type === SuperCutSegmentationType.Caption && (
                            <Detail>
                                <PaddedPillBlock>CC</PaddedPillBlock>
                            </Detail>
                        )}
                        <Detail>
                            <Icon className={SuperCutUpload.icon(source)} title={SuperCutUpload.title(source)} />
                        </Detail>
                        <Detail>
                            <StatusBox status={status} />
                        </Detail>
                    </CaptureRow>
                )
            })}
        </>
    )
}

type CompletedProps = {
    captures: ICapture[]
    onDeleteClicked: (event: MouseEvent, capture: ICapture) => void
    showUsername: boolean
}
export const CompletedCaptureList = ({ captures, onDeleteClicked, showUsername }: CompletedProps) => {
    return (
        <>
            {captures.map((c: ICapture) => {
                const status = SuperCutStatus.getNormalizedStatus(c.status)
                const source = SuperCutUpload.getNormalizedUploadType(c.source)

                return (
                    <CaptureRow key={c.id} href={`/supercut/detail/${c.id}`} target="_blank" rel="noopener noreferrer">
                        <Thumbnail src={c.thumbnail} />
                        <TitleContainer>
                            <CaptureTitle>{c.title}</CaptureTitle>
                            <PaddedPillBlock isWide>
                                {c.segments_count >= 999 ? '999+' : c.segments_count}
                            </PaddedPillBlock>
                            {c.process_type === SuperCutSegmentationType.Caption && (
                                <PaddedPillBlock>CC</PaddedPillBlock>
                            )}
                        </TitleContainer>

                        <Delete onClick={evt => onDeleteClicked(evt, c)}>
                            <span className="ss-trash" title="Delete This Capture" />
                        </Delete>
                        {showUsername && !!c.username && <Detail>{`@${c.username}`}</Detail>}
                        <Detail>{dateFormat(new Date(c.date), 'mmm dS, yyyy')}</Detail>
                        <Detail>
                            <Icon className={SuperCutUpload.icon(source)} title={SuperCutUpload.title(source)} />
                        </Detail>
                        <Detail>
                            <StatusBox status={status} />
                        </Detail>
                    </CaptureRow>
                )
            })}
        </>
    )
}
